import React, { createContext, useContext, useState, useEffect } from 'react';
import { io } from 'socket.io-client';
import client from '../api/client';
import { BASE_URL } from '../config/api';

const CallsContext = createContext();

const getSocketServerUrl = () => {
  try {
    return new URL(BASE_URL).origin;
  } catch (_) {
    return BASE_URL;
  }
};

export const useCalls = () => {
  const context = useContext(CallsContext);
  if (!context) {
    throw new Error('useCalls doit être utilisé dans CallsProvider');
  }
  return context;
};

export const CallsProvider = ({ children }) => {
  const [activeCalls, setActiveCalls] = useState([]);
  const [incomingCall, setIncomingCall] = useState(null);
  const [socket, setSocket] = useState(null);
  // Extension suivie pour les appels (par défaut 303, ou valeur stockée)
  const [extension, setExtension] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('softphone_extension') || '303';
    }
    return '303';
  });
  const [stats, setStats] = useState({
    today: {
      total_calls: 0,
      completed_calls: 0,
      active_calls: 0,
      avg_duration: 0,
      incoming_calls: 0,
      outgoing_calls: 0,
    },
  });

  // Connexion Socket.IO
  useEffect(() => {
    const socketInstance = io(getSocketServerUrl(), {
      transports: ['websocket'],
    });

    socketInstance.on('connect', () => {
      // Rejoindre la room des appels pour l'extension courante
      const currentExt = window.localStorage.getItem('softphone_extension') || extension;
      socketInstance.emit('join_calls', { extension: currentExt });
    });

    // Écouter les appels entrants
    socketInstance.on('incomingCall', (data) => {
      // Récupérer l'extension actuelle depuis localStorage (plus fiable que la closure)
      const currentExt = window.localStorage.getItem('softphone_extension') || extension;
      
      // Ne s'intéresser qu'aux appels destinés à notre extension
      if (data.extension && String(data.extension) !== String(currentExt)) {
        return;
      }

      setIncomingCall(data);

      // Ajouter à la liste des appels actifs
      setActiveCalls(prev => {
        const exists = prev.find(call => call.channel === data.channel);
        if (exists) return prev;
        return [...prev, { ...data, status: 'ringing' }];
      });

      // Notification sonore
      playRingtone();
    });

    // Écouter les appels terminés
    socketInstance.on('callEnded', (data) => {
      setActiveCalls(prev => prev.filter(call => call.channel !== data.channel));
      
      // Si c'était l'appel entrant actuel, le retirer
      if (incomingCall && incomingCall.channel === data.channel) {
        setIncomingCall(null);
        stopRingtone();
      }

      // Rafraîchir les stats
      fetchStats();
    });

    // Écouter les appels répondus
    socketInstance.on('callAnswered', (data) => {
      setActiveCalls(prev =>
        prev.map(call =>
          call.channel === data.channel ? { ...call, status: 'answered' } : call
        )
      );
      
      if (incomingCall && incomingCall.channel === data.channel) {
        setIncomingCall(null);
        stopRingtone();
      }
    });

    socketInstance.on('disconnect', () => {
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
      stopRingtone();
    };
  }, []);

  // Mettre à jour la room et rafraîchir les données quand l'extension change
  useEffect(() => {
    if (!socket) return;
    try {
      // Utiliser l'extension depuis localStorage si disponible, sinon depuis l'état
      const currentExt = window.localStorage.getItem('softphone_extension') || extension;
      socket.emit('join_calls', { extension: currentExt });
      fetchStats();
      fetchActiveCalls();
    } catch (error) {
      console.error('Erreur lors de la mise à jour de l\'extension dans CallsContext:', error);
    }
  }, [extension, socket]);

  // Charger les statistiques
  const fetchStats = async () => {
    try {
      const currentExt = window.localStorage.getItem('softphone_extension') || extension;
      const response = await client.get(`/api/calls/stats?extension=${currentExt}`);
      setStats(response.data);
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  // Charger les appels actifs
  const fetchActiveCalls = async () => {
    try {
      const response = await client.get('/api/calls/active');
      setActiveCalls(response.data);
    } catch (error) {
      console.error('Erreur chargement appels actifs:', error);
    }
  };

  // Charger au démarrage
  useEffect(() => {
    fetchStats();
    fetchActiveCalls();

    // Rafraîchir toutes les 30 secondes
    const interval = setInterval(() => {
      fetchStats();
      fetchActiveCalls();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  // Passer un appel
  const makeCall = async (from, to) => {
    try {
      const response = await client.post('/api/calls/dial', { from, to });
      return response.data;
    } catch (error) {
      console.error('Erreur lors de l\'appel:', error);
      throw error;
    }
  };

  // Répondre à un appel
  const answerCall = async (channel, ext = null) => {
    try {
      const currentExt = ext || window.localStorage.getItem('softphone_extension') || extension;
      const response = await client.post('/api/calls/answer', { channel, extension: currentExt });
      setIncomingCall(null);
      stopRingtone();
      return response.data;
    } catch (error) {
      // Si l'appel n'existe déjà plus, nettoyer l'UI
      if (error.response?.status === 404) {
        setIncomingCall(null);
        setActiveCalls(prev => prev.filter(call => call.channel !== channel));
        stopRingtone();
        return { success: false, alreadyEnded: true };
      }
      console.error('Erreur répondre appel:', error);
      throw error;
    }
  };

  // Raccrocher
  const hangupCall = async (channel) => {
    try {
      const response = await client.post('/api/calls/hangup', { channel });
      setActiveCalls(prev => prev.filter(call => call.channel !== channel));
      if (incomingCall && incomingCall.channel === channel) {
        setIncomingCall(null);
        stopRingtone();
      }
      return response.data;
    } catch (error) {
      // Si l'appel n'existe déjà plus, le retirer quand même de l'UI
      if (error.response?.status === 404) {
        setActiveCalls(prev => prev.filter(call => call.channel !== channel));
        if (incomingCall && incomingCall.channel === channel) {
          setIncomingCall(null);
          stopRingtone();
        }
        return { success: false, alreadyEnded: true };
      }
      console.error('Erreur raccrochage:', error);
      throw error;
    }
  };

  // Rejeter un appel entrant
  const rejectCall = async (channel) => {
    try {
      await hangupCall(channel);
      setIncomingCall(null);
      stopRingtone();
    } catch (error) {
      console.error('Erreur rejet appel:', error);
      throw error;
    }
  };

  // Sonnerie : désactivée ici, le bip est géré côté UI (missions) pour être mieux contrôlé
  const playRingtone = () => {
    // Intentionnellement vide : la sonnerie visuelle/sonore est gérée au niveau des composants (ex: missions)
  };

  const stopRingtone = () => {
    // Intentionnellement vide, voir playRingtone ci‑dessus
  };

  // Fonction wrapper pour mettre à jour l'extension (état + localStorage)
  const updateExtension = (newExt) => {
    const extStr = String(newExt).trim();
    setExtension(extStr);
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem('softphone_extension', extStr);
    }
  };

  return (
    <CallsContext.Provider
      value={{
        activeCalls,
        incomingCall,
        extension,
        setExtension: updateExtension,
        stats,
        makeCall,
        answerCall,
        hangupCall,
        rejectCall,
        fetchStats,
        fetchActiveCalls,
      }}
    >
      {children}
    </CallsContext.Provider>
  );
};
